import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { useSession } from '../../context/SessionContext';
import { AntiCheating } from '../utils';
import { Footer, ConfirmationScreen, AbandonedScreen, CompletedScreen, AbandonConfirmationModal, StatusPill, CompletionProgressBar, StickyMenu, ReportQuestionPopup } from '../ui';
import { EVALUATION_ENDPOINTS } from '../../constants/apiEndpoints';
import { useAbandonConfirmation, useNavigation, useCheatingDetection, useReportPopup, useNavigationPrompt } from '../../hooks';
import { AssessmentLoadingModal, PreviousQuestionsAccordion, QuestionRenderer, CompletionCircle, SkillsBadges, ElapsedTime } from './components/ChallengeInterview';

function ChallengeInterview() {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const { sessionData, updateSessionData } = useSession();
  const { navigatePrevious } = useNavigation();
  
  // Get eid from URL path parameter
  const eid = params.eid;
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [challengeInstanceId, setChallengeInstanceId] = useState(null);
  const [challengeStatus, setChallengeStatus] = useState(sessionData?.challenge_status || 'cs_not_started');
  const [challengeStartDate, setChallengeStartDate] = useState(null);
  const [currentResponse, setCurrentResponse] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [elapsedTime, setElapsedTime] = useState('');
  const [evaluationCategory, setEvaluationCategory] = useState("Software Development / Engineering");
  
  // Cheating detection
  const { cheatingEvents, handleCheatingDetected, cleanup } = useCheatingDetection();

  // Report popup hook
  const { reportPopupOpen, currentQuestionId, currentQuestion, openReportPopup, closeReportPopup } = useReportPopup();
  
  // Prevent refresh/navigation during submission
  useNavigationPrompt(isSubmitting);
  
  // State for collapsible previous questions
  const [collapsedSections, setCollapsedSections] = useState({
    mainQuestion: true,
    followupQuestions: {}
  });

  // Toggle collapse state for sections
  const toggleCollapse = (section, index = null) => {
    setCollapsedSections(prev => {
      if (section === 'mainQuestion') {
        return {
          ...prev,
          mainQuestion: !prev.mainQuestion
        };
      } else if (section === 'followupQuestion') {
        return {
          ...prev,
          followupQuestions: {
            ...prev.followupQuestions,
            [index]: !prev.followupQuestions[index]
          }
        };
      }
      return prev;
    });
  };


  // Calculate the number of completed questions
  const getCompletedQuestionsCount = () => {
    if (!data || !data.questions) return 0;
    
    // Count questions that have completed=true
    return data.questions.reduce((count, question) => {
      return question.completed === true ? count + 1 : count;
    }, 0);
  };


  // Function to start the challenge
  const startChallenge = async () => {
    setLoading(true);
    try {
      // Send request to start the challenge
      const response = await fetch(
        `${EVALUATION_ENDPOINTS.START}?ci_id=${challengeInstanceId}`
      );

      if (!response.ok) {
        throw new Error(`Failed to start challenge: ${response.status} ${response.statusText}`);
      }

      const responseData = await response.json();
      const startResponse = responseData[0];
      
      setChallengeStatus(startResponse.challenge_status);
      
      // Update session data with the new status
      updateSessionData({ 
        challenge_status: startResponse.challenge_status
      });
    } catch (err) {
      console.error('Error starting challenge:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle previous navigation
  const handlePrevious = () => {
    navigatePrevious(location.pathname);
  };


  // Abandon confirmation hook
  const { 
    showAbandonConfirm, 
    handleAbandonClick, 
    handleCancelAbandon, 
    handleConfirmAbandon 
  } = useAbandonConfirmation(challengeInstanceId, eid, EVALUATION_ENDPOINTS.ABANDON, 'challenge_status', 'cs_abandoned');

  // Find the first incomplete question (using array order)
  const getCurrentQuestion = () => {
    if (!data?.questions) return null;
    
    // COMMENTED OUT: ID-based sorting (kept for potential reversion)
    // const sortedQuestions = [...data.questions].sort((a, b) => a.id - b.id);
    // const incompleteQuestion = sortedQuestions.find(question => question.completed === false);
    
    // NEW: Use array index order instead of ID sorting
    const incompleteQuestion = data.questions.find(question => question.completed === false);
    
    if (!incompleteQuestion) return null;
    
    // Check if main question has an answer based on question type
    let hasMainAnswer = false;
    if (incompleteQuestion.type === 'multiple-answer') {
      // For multiple-answer questions, check both answer array and otherAnswer
      const hasSelectedOptions = incompleteQuestion.answer && Array.isArray(incompleteQuestion.answer) && incompleteQuestion.answer.length > 0;
      const hasOtherInput = incompleteQuestion.otherAnswer && typeof incompleteQuestion.otherAnswer === 'string' && incompleteQuestion.otherAnswer.trim() !== '';
      hasMainAnswer = hasSelectedOptions || hasOtherInput;
    } else if (incompleteQuestion.type === 'single-answer') {
      // For single-answer questions, check both answer string and otherAnswer
      const hasSelectedOption = incompleteQuestion.answer && typeof incompleteQuestion.answer === 'string' && incompleteQuestion.answer.trim() !== '';
      const hasOtherInput = incompleteQuestion.otherAnswer && typeof incompleteQuestion.otherAnswer === 'string' && incompleteQuestion.otherAnswer.trim() !== '';
      hasMainAnswer = hasSelectedOption || hasOtherInput;
    } else {
      // For text-based questions, answer should be a string
      hasMainAnswer = incompleteQuestion.answer && typeof incompleteQuestion.answer === 'string' && incompleteQuestion.answer.trim() !== '';
    }
    
    // If main question has no answer, show main question
    if (!hasMainAnswer) {
      return {
        ...incompleteQuestion,
        is_main_question: true
      };
    }
    
    // If main question has answer, check for followup questions
    if (incompleteQuestion.followup_questions_and_answers && incompleteQuestion.followup_questions_and_answers.length > 0) {
      // Find first followup without answer_text
      const incompleteFollowupIndex = incompleteQuestion.followup_questions_and_answers.findIndex(
        followup => !followup.answer_text || followup.answer_text.trim() === ''
      );
      
      if (incompleteFollowupIndex !== -1) {
        return {
          ...incompleteQuestion,
          is_main_question: false,
          current_followup_index: incompleteFollowupIndex
        };
      }
    }
    
    // If we get here, main question is answered and no incomplete followups exist
    // This question should be marked as completed
    return null;
  };

  /**
   * Memoization for video/audio re-render prevention
   * - handleChange: Stable function reference prevents QuestionRenderer from re-creating onChange
   * - memoizedCurrentQuestion: Prevents unnecessary recalculation of current question
   */
  const handleChange = useCallback((questionId, value) => {
    setCurrentResponse(value);
  }, []);

  // Check if current response is valid for submission
  const isResponseValid = () => {
    const currentQuestion = getCurrentQuestion();
    if (!currentQuestion) return false;

    // Get the question type and userInput based on whether it's main or followup
    const isFollowup = !currentQuestion.is_main_question;
    const questionType = isFollowup 
      ? currentQuestion.followup_questions_and_answers[currentQuestion.current_followup_index].type 
      : currentQuestion.type;
    
    const userInput = isFollowup 
      ? currentQuestion.followup_questions_and_answers[currentQuestion.current_followup_index].userInput 
      : currentQuestion.userInput;

    // console.log('Validating response:', { questionType, userInput, currentResponse, isArray: Array.isArray(currentResponse) });

    // For audio/video input or audio/video-answer type, check if we have a valid blob URL
    if (userInput === 'audio' || questionType === 'audio-answer' ||
        userInput === 'video' || questionType === 'video-answer') {
      return currentResponse && typeof currentResponse === 'string' && currentResponse.startsWith('blob:');
    }

    // For multiple-answer and single-answer questions with otherAnswer field
    if (questionType === 'multiple-answer' || questionType === 'single-answer') {
      if (!currentResponse) return false;
      
      let hasSelectedOptions = false;
      if (questionType === 'multiple-answer') {
        // For multiple-answer, answer is an array
        hasSelectedOptions = currentResponse.answer && Array.isArray(currentResponse.answer) && currentResponse.answer.length > 0;
      } else if (questionType === 'single-answer') {
        // For single-answer, answer is a string
        hasSelectedOptions = currentResponse.answer && typeof currentResponse.answer === 'string' && currentResponse.answer.trim().length > 0;
      }
      
      const hasOtherInput = currentResponse.otherAnswer && typeof currentResponse.otherAnswer === 'string' && currentResponse.otherAnswer.trim().length > 0;
      
      return hasSelectedOptions || hasOtherInput;
    }

    // For other question types, check if response exists and has at least one non-whitespace character
    return currentResponse && typeof currentResponse === 'string' && currentResponse.trim().length > 0;
  };

  // Submit current question function
  const submitQuestion = async () => {
    // === VALIDATION & SETUP ===
    const currentQuestion = getCurrentQuestion();
    if (!currentQuestion || !challengeInstanceId) {
      console.error('No current question or challenge instance ID');
      return;
    }

    setIsSubmitting(true);

    try {
      // === PREPARE QUESTION DATA ===
      const questionCopy = JSON.parse(JSON.stringify(currentQuestion));
      const isMainQuestion = currentQuestion.is_main_question;
      
      // Get the question type and followup index based on whether it's main or followup
      const questionType = isMainQuestion 
        ? currentQuestion.type 
        : currentQuestion.followup_questions_and_answers[currentQuestion.current_followup_index].type;
      
      // Check if response is a blob URL (audio or video)
      const isBlobResponse = typeof currentResponse === 'string' && currentResponse.startsWith('blob:');
      const isVideoResponse = isBlobResponse && questionType === 'video-answer';
      const isAudioResponse = isBlobResponse && questionType === 'audio-answer';
      
      // This is the array index of the followup question (or null if it's not a followup question)
      const followupIndex = !isMainQuestion 
        ? currentQuestion.current_followup_index 
        : null;

      // Update question with answer based on type
      if (isMainQuestion) {
        // Main question answers
        if (currentQuestion.type === 'multiple-answer') {
          questionCopy.answer = currentResponse.answer || [];
          questionCopy.otherAnswer = currentResponse.otherAnswer || '';
        } else if (currentQuestion.type === 'single-answer') {
          questionCopy.answer = currentResponse.answer || '';
          questionCopy.otherAnswer = currentResponse.otherAnswer || '';
        } else {
          questionCopy.answer = currentResponse || '';
        }
      } else {
        // Followup question answers
        const followupIndex = currentQuestion.current_followup_index;
        const followupType = currentQuestion.followup_questions_and_answers[followupIndex].type;
        
        if (followupType === 'multiple-answer') {
          questionCopy.followup_questions_and_answers[followupIndex].answer_text = currentResponse.answer || [];
          questionCopy.followup_questions_and_answers[followupIndex].other_answer_text = currentResponse.otherAnswer || '';
        } else if (followupType === 'single-answer') {
          questionCopy.followup_questions_and_answers[followupIndex].answer_text = currentResponse.answer || '';
          questionCopy.followup_questions_and_answers[followupIndex].other_answer_text = currentResponse.otherAnswer || '';
        } else {
          questionCopy.followup_questions_and_answers[followupIndex].answer_text = currentResponse || '';
        }
      }

      // Clean up question object for submission
      const questionWithAnswer = {
        ...questionCopy,
        is_main_question: undefined,
        current_followup_index: undefined
      };

      // console.log('Submitting Question Data:', JSON.stringify(questionWithAnswer, null, 2));

      // === SUBMIT TO SERVER ===
      let response;
      
      if (isAudioResponse || isVideoResponse) {
        // Audio/Video submission with FormData
        const mediaBlob = await fetch(currentResponse).then(res => res.blob());
        const formData = new FormData();
        formData.append('ci_id', challengeInstanceId);
        formData.append('questionAnswers', JSON.stringify(questionWithAnswer));
        formData.append('cheatingEvents', JSON.stringify([...cheatingEvents]));
        formData.append('is_main_question', isMainQuestion);
        formData.append('question_type', questionType);
        formData.append('followup_index', followupIndex);
        
        if (isVideoResponse) {
          formData.append('video', mediaBlob, 'video-answer.webm');
          console.log('Submitting video data with FormData');
        } else {
          formData.append('audio', mediaBlob, 'audio-answer.webm');
          console.log('Submitting audio data with FormData');
        }
        
        response = await fetch(EVALUATION_ENDPOINTS.SUBMIT_QUESTION, {
          method: 'POST',
          body: formData
        });
      } else {
        // Regular JSON submission
        const payload = {
          ci_id: challengeInstanceId,
          questionAnswers: questionWithAnswer,
          cheatingEvents: [...cheatingEvents],
          is_main_question: isMainQuestion,
          question_type: questionType,
          followup_index: followupIndex
        };
        
        response = await fetch(EVALUATION_ENDPOINTS.SUBMIT_QUESTION, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      // === HANDLE RESPONSE ===
      if (!response.ok) {
        throw new Error(`Failed to submit question: ${response.status} ${response.statusText}`);
      }

      const responseData = await response.json();
      console.log("Question submission responseData:", responseData);

      // Update local state with server response
      setData(responseData[0].challenge_assessed_json);
      
      // Update challenge status from server response (like Challenge.js does)
      if (responseData[0].challenge_status) {
        console.log('Updating challenge status from server:', responseData[0].challenge_status);
        setChallengeStatus(responseData[0].challenge_status);
        updateSessionData({ challenge_status: responseData[0].challenge_status });
      }
      
      setCurrentResponse('');

    } catch (err) {
      console.error('Error submitting question:', err);
      alert("There was an error submitting your answer. Please try again."); // TODO: replace with toast notification
    } finally {
      setIsSubmitting(false);
    }
  };

  // Memoize current question to prevent unnecessary re-renders
  const memoizedCurrentQuestion = useMemo(() => getCurrentQuestion(), [data]);

  // Render a single question (adapted from Challenge.js)
  const renderQuestionItem = (question) => {
    if (!question) return null;
    
    // Determine if this is a followup question and get the appropriate data
    const isFollowup = !question.is_main_question;
    const followupData = isFollowup ? question.followup_questions_and_answers[question.current_followup_index] : null;
    
    // Get the question type and text based on whether it's main or followup
    let questionType = isFollowup ? followupData.type : question.type;
    let questionText = isFollowup ? followupData.question_text : question.questionText;
    
    return (
      <div key={question.id} className="w-full">
        {/* Previous Questions Accordion */}
        <PreviousQuestionsAccordion 
          question={question}
          collapsedSections={collapsedSections}
          toggleCollapse={toggleCollapse}
        />
        
        {/* Question Renderer */}
        <QuestionRenderer 
          question={question}
          currentResponse={currentResponse}
          handleChange={handleChange}
        />
      </div>
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      // Skip if no eid
      if (!eid) return;
      
      // Check if both questionnaire_status and challenge_status are "Done"
      if (sessionData?.questionnaire_status === 'qs_done' && sessionData?.challenge_status === 'cs_done') {
        navigate(`/finished/${eid}`);
        return;
      }
      
      try {
        const response = await fetch(
          `${EVALUATION_ENDPOINTS.GET}?eid=${eid}`
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch challenge data: ${response.status} ${response.statusText}`);
        }

        const responseData = await response.json();
        console.log("Challenge Interview responseData:", responseData);
        
        // Get the challenge response data with optional chaining
        const challengeResponse = responseData?.[0];
        if (!challengeResponse) {
          throw new Error('No challenge data found in response');
        }
        
        // QUESTIONNAIRE SKIPPING: Check if questionnaire is completed (only if questionnaire step is enabled)
        if (challengeResponse.steps?.step_questionnaire && 
            challengeResponse.questionnaire_status !== 'qs_done') {
          navigate(`/questionnaire/${eid}`);
          return;
        }
        
        // Set status first - this is important for UI rendering
        setChallengeStatus(challengeResponse.challenge_status);
        updateSessionData({ challenge_status: challengeResponse.challenge_status });
        
        // Set other basic data
        setChallengeInstanceId(challengeResponse.ci_id);
        setChallengeStartDate(challengeResponse.challenge_start_date);
        
        // Set evaluation category from evaluation_role.mainCategory
        if (challengeResponse.evaluation_role?.mainCategory) {
          setEvaluationCategory(challengeResponse.evaluation_role.mainCategory);
        }
        
        // Only try to set challenge data if it exists
        if (challengeResponse.challenge_assessed_json) {
          setData(challengeResponse.challenge_assessed_json);
        }
        
      } catch (err) {
        console.error('Error fetching challenge data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [eid, sessionData?.challenge_status, navigate]);

  // Cleanup effect to clear pending timeouts when component unmounts
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg border border-gray-200">
          <div className="inline-block h-16 w-16 animate-spin rounded-full border-4 border-solid border-gray-500 border-r-transparent mb-4"></div>
          <h2 className="text-2xl font-bold text-gray-800 mb-3">Loading Challenge...</h2>
          <p className="text-gray-600 max-w-md">Please wait while we prepare your challenge.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen p-0 pt-20 pb-16 w-full">
      {/* Sticky Top Menu (only shown when active) */}
      <StickyMenu 
        startDate={challengeStatus === 'cs_started' ? null : null}
        timerDuration={challengeStatus === 'cs_started' ? null : null}
        onTimeUp={() => {}}
      />
      
      {/* 2-Column Layout Container */}
      <div className="flex-1 flex justify-center px-4">
        <div className={`w-full bg-white rounded-3xl shadow-md flex ${
          challengeStatus === 'cs_started' ? 'max-w-[1100px]' : 'max-w-[800px]'
        }`}>
          {/* Left Column - Main Content */}
          <div className={`flex-1 py-12 px-8 flex flex-col items-center relative ${
            challengeStatus === 'cs_started' ? 'border-r border-gray-200' : ''
          }`}>
        {/* Show appropriate header based on challenge status */}
        {challengeStatus === 'cs_not_started' ? (
          /* Simple title for not started state */
          <div className="relative w-full mb-4">
            <h2 className="text-3xl font-semibold text-center">Challenge</h2>
          </div>
        ) : challengeStatus === 'cs_started' && getCurrentQuestion() ? (
          /* Detailed question header when actively answering questions */
          <>
            <div className="relative w-full mb-4">
              <h2 className="text-2xl font-normal text-center">
                Challenge Question <span className="font-bold">{getCompletedQuestionsCount() + 1}/{data?.questions?.length || 0}</span>
              </h2>
              {(() => {
                const currentQuestion = getCurrentQuestion();
                if (!currentQuestion) return null;
                
                return (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      openReportPopup(currentQuestion.id, currentQuestion);
                    }}
                    className="absolute right-0 top-1/2 transform -translate-y-1/2 text-sm bg-white hover:bg-gray-50 text-black border border-black px-3 py-1 rounded-full flex items-center transition-colors duration-200"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    Report
                  </button>
                );
              })()}
            </div>
            
            {/* Display skills tested for current question - always from main question */}
            <SkillsBadges skills={getCurrentQuestion()?.skillsTested} />
          </>
        ) : null}
        
        {/* Anti-cheating component */}
        <AntiCheating onCheatingDetected={handleCheatingDetected} />
        
        {/* Report Question Popup */}
        <ReportQuestionPopup 
          isOpen={reportPopupOpen} 
          onClose={closeReportPopup} 
          questionId={currentQuestionId}
          eval_id={eid}
          type="Challenge"
          question={currentQuestion}
        />
        
        {/* If challenge is not started, show confirmation screen */}
        {challengeStatus === 'cs_not_started' && !loading ? (
          <ConfirmationScreen
            title="Ready To Start?"
            description="Once started, please aim to complete the challenge in one session."
            timeRequired="No time limit"
            onStart={startChallenge}
            onBack={handlePrevious}
            startButtonText="Start Challenge"
            backButtonText="Back"
            icon="question"
            faqType="challenge"
          />
        ) : challengeStatus === 'cs_abandoned' ? (
          // NOTE: This AbandonedScreen is currently unreachable because WelcomeScreen.js 
          // handles all abandoned states globally. To reach this code, you would need to:
          // 1. Comment out the abandoned check in WelcomeScreen.js
          // 2. Remove the abandoned redirect restriction in ProtectedRoute.js
          <AbandonedScreen
            title="Challenge Abandoned"
            message="This challenge has been abandoned."
            onBack={() => navigate(`/${eid}`)}
            backButtonText="Start Page"
          />
        ) : challengeStatus === 'cs_done' ? (
          <CompletedScreen
            title="Challenge Completed"
            message="You have already completed this challenge."
            onContinue={() => navigate(`/finished/${eid}`)}
            continueButtonText="View Results"
          />
        ) : error ? (
          <div className="text-lg text-red-500">Error: {error}</div>
        ) : (
          <div className="w-full flex flex-col flex-1">
            {/* Current Question Display - Content Area */}
            <div className="flex-1">
              {memoizedCurrentQuestion ? (
                <div className="w-full">
                  {/* Render the current question */}
                  {renderQuestionItem(memoizedCurrentQuestion)}
                </div>
              ) : null}
            </div>
            
            {/* Submit Button Area - Always at Bottom */}
            {memoizedCurrentQuestion ? (
              <div className="mt-auto pt-8">
                  {/* Submit button centered */}
                  <div className="flex justify-center w-full">
                    <button
                      type="button"
                      onClick={submitQuestion}
                      disabled={isSubmitting || !isResponseValid()}
                      className={`text-white rounded-lg px-8 py-4 text-xl shadow-md hover:shadow-lg flex items-center ${
                        isSubmitting || !isResponseValid() 
                          ? 'bg-gray-400 cursor-not-allowed' 
                          : 'bg-blue-600 hover:bg-blue-700 transition-colors duration-200'
                      }`}
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="inline-block animate-spin mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Assessing...
                        </>
                      ) : (
                        <div className="flex flex-col items-center">
                          <div className="flex items-center text-xl">
                            Submit Answer
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                            </svg>
                          </div>
                          <span className="text-xs mt-1 opacity-70">
                            Assessing can take up to 60 sec
                          </span>
                        </div>
                      )}
                    </button>
                  </div>
                  
                  {/* Bottom section with centered info text */}
                  <div className="flex justify-center items-center mt-4">
                    <p className="text-sm text-gray-500">
                      Answer "I don't know" to skip.
                    </p>
                  </div>
                </div>
            ) : null}
          </div>
        )}
          </div>
          
          {/* Right Column - Sidebar - Only show when challenge is started */}
          {challengeStatus === 'cs_started' && (
            <div 
              className="w-80 py-10 px-8 rounded-r-3xl flex flex-col justify-between relative" 
              style={{ 
                backgroundColor: '#F9F9F9',
                backgroundImage: 'url(/leftbg.svg), url(/hotcircle.svg)',
                backgroundSize: '120% auto, auto',
                backgroundRepeat: 'no-repeat, no-repeat',
                backgroundPosition: 'top center, bottom center'
              }}
            >
            <div className="space-y-8">
              {/* Validate your skills section */}
              <div>
                <h2 className="text-2xl font-regular text-gray-900 mb-4 leading-tight">
                  Validate your skills and knowledge.
                </h2>
                <p className="text-gray-600 text-sm">
                  This challenge is assessed and scored.
                </p>
              </div>

              {/* Challenge Status section */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  Challenge Status
                </h3>
                <StatusPill status={challengeStatus} />
              </div>

              {/* Elapsed Time section - only show if challenge has started */}
              <ElapsedTime startDate={challengeStartDate} />

              {/* Completion Rate section */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  Completion Progress
                </h3>
                <div className="flex justify-center">
                  <CompletionCircle 
                    completedQuestions={getCompletedQuestionsCount()} 
                    totalQuestions={data?.questions?.length || 0} 
                  />
                </div>
              </div>

            </div>

            {/* Bottom section with links and footer */}
            <div className="space-y-6">
              {/* Cheating monitoring warning */}
              <div className="rounded-lg p-3 mb-12 mt-8 relative" style={{ backgroundColor: '#FEF3C7' }}>
                <div className="flex items-center">
                  <img 
                    src="/nocheatingicon.svg" 
                    alt="Fingers crossed" 
                    className="w-8 h-8 mr-3 flex-shrink-0"
                    style={{ filter: 'brightness(0) saturate(100%) invert(8%) sepia(50%) saturate(7500%) hue-rotate(0deg) brightness(95%) contrast(107%)' }}
                  />
                  <p className="text-sm text-left" style={{ color: '#7C0000' }}>
                    Integrity and authenticity checks are enabled.
                  </p>
                </div>
              </div>
              {/* Action links */}
              <div className="flex justify-between items-center px-2">
                <button
                  onClick={handleAbandonClick}
                  className="flex items-center text-red-600 hover:text-red-700 text-sm transition-colors duration-200"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Abandon
                </button>
                
                <button className="flex items-center text-gray-600 hover:text-gray-700 text-sm transition-colors duration-200">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Help
                </button>
              </div>

              {/* Footer */}
              <div className="border-t border-gray-300 pt-4">
                <div className="text-xs text-gray-500 text-center">
                  Powered by tendent.ai
                </div>
              </div>
            </div>
          </div>
          )}
        </div>
      </div>
      
      {/* Abandon Confirmation Modal */}
      <AbandonConfirmationModal
        isOpen={showAbandonConfirm}
        onCancel={handleCancelAbandon}
        onConfirm={handleConfirmAbandon}
        title="Abandon Challenge?"
        message="Are you sure you want to abandon this challenge? This action cannot be undone and your progress will be lost."
      />      
      
      {/* Assessment Loading Modal */}
      <AssessmentLoadingModal isSubmitting={isSubmitting} category={evaluationCategory} />


    </div>
  );
}

export default ChallengeInterview;
